import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { VoiceRecorder } from "../components/VoiceRecorder";
import { Button } from "../components/ui/button";
import { Camera, Car, AlertOctagon, Truck, Trash2, Wrench, Droplet, ShieldAlert, LogOut } from "lucide-react";
import { toast } from "sonner";

const QUICK = [
  { key: "visitor", label: "Visitor", icon: ShieldAlert, category: "security" },
  { key: "parking", label: "Parking", icon: Car, category: "parking" },
  { key: "delivery", label: "Delivery", icon: Truck, category: "vendor" },
  { key: "housekeeping", label: "Housekeeping", icon: Trash2, category: "housekeeping" },
  { key: "maintenance", label: "Maintenance", icon: Wrench, category: "admin" },
  { key: "water", label: "Water", icon: Droplet, category: "water" },
];

export default function StaffHomePage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [transcript, setTranscript] = useState("");
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null);

  async function logIncident(category) {
    if (!transcript.trim()) {
      toast.error("Please record what happened first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/issues", {
        title: transcript.split(".")[0].slice(0, 80),
        description: transcript,
        category,
        priority: "medium",
        is_common_area: true,
        audio_transcription: transcript,
      });
      toast.success("Incident logged");
      setTranscript(""); setActive(null);
      setTimeout(() => nav(`/issues/${data.id}`), 800);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  }

  async function handlePhoto(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/upload", fd); toast.success("Photo saved"); }
    catch (err) { toast.error(formatApiError(err)); }
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="px-5 py-4 bg-white border-b border-stone-200 flex items-center justify-between">
        <div>
          <div className="text-xs text-stone-500">Staff mode</div>
          <div className="font-serif-display text-lg font-semibold">{user?.name}</div>
        </div>
        <Button onClick={async () => { await logout(); nav("/login"); }} data-testid="staff-logout"
          variant="ghost" size="icon" className="rounded-full"><LogOut size={20} /></Button>
      </header>

      <main className="p-5 pb-10">
        <div className="bg-white rounded-3xl border border-stone-200 p-6 flex flex-col items-center text-center" data-testid="staff-record">
          <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Tap & speak</div>
          <h1 className="font-serif-display text-2xl font-semibold mt-1">Log an incident</h1>
          <div className="my-6">
            <VoiceRecorder onTranscript={setTranscript} testid="staff-mic" />
          </div>
          {transcript && (
            <div className="w-full bg-[#FDFBF7] rounded-xl p-3 border border-stone-200 text-sm text-stone-800" data-testid="staff-transcript">
              "{transcript}"
            </div>
          )}
          <div className="text-xs text-stone-500 mt-3">Hindi or English. Translation handled automatically.</div>
        </div>

        <h2 className="font-serif-display text-xl font-medium mt-7 mb-3">What is it about?</h2>
        <div className="grid grid-cols-2 gap-3" data-testid="staff-categories">
          {QUICK.map((q) => (
            <button key={q.key} onClick={() => { setActive(q.key); logIncident(q.category); }}
              disabled={busy || !transcript}
              data-testid={`quick-${q.key}`}
              className="aspect-square bg-white border-2 border-stone-200 rounded-3xl flex flex-col items-center justify-center gap-3 disabled:opacity-50 hover:border-[#C05640] hover:bg-[#FDFBF7] active:scale-95 transition">
              <q.icon size={42} className="text-[#C05640]" strokeWidth={1.5} />
              <span className="font-semibold text-stone-900 text-base">{q.label}</span>
            </button>
          ))}
        </div>

        <label className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-3xl border-2 border-dashed border-stone-300 text-stone-700 font-medium cursor-pointer" data-testid="staff-photo">
          <Camera size={22} /> Add photo (optional)
          <input type="file" accept="image/*" capture="environment" hidden onChange={handlePhoto} />
        </label>

        <button className="mt-3 w-full py-5 rounded-2xl bg-red-600 text-white font-bold text-lg active:scale-95 transition"
          onClick={() => logIncident("security")} data-testid="staff-emergency">
          <AlertOctagon className="inline mr-2" size={22} /> Emergency report
        </button>
      </main>
    </div>
  );
}
